import { useEffect, useRef, useState, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { FaSearch } from "react-icons/fa";

import useSite from "hooks/use-site";
import useSearch, { SEARCH_STATE_LOADED } from "hooks/use-search";
import { postPathBySlug } from "lib/posts";

import Section from "components/Section";
import Logo from "components/Logo";
import useMedia from "hooks/use-media";
import Hamburger from "components/Hamburger";
import Triangle from "components/Triangle";

import styles from "./Nav.module.scss";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getMenuBylocationV2 } from "data/fetchPlugin";

const SEARCH_VISIBLE = "visible";
const SEARCH_HIDDEN = "hidden";
const SHOW_SEARCH = false;

const Nav = ({
    isPreview,
    navStaticColor = false,
    isHomePage,
    isFullColor,
    templateType = "default",
}) => {
    const router = useRouter();
    const formRef = useRef();

    const [searchVisibility, setSearchVisibility] = useState(SEARCH_HIDDEN);

    const [menuPrimary, setMenuPrimary] = useState(null);

    const { locale, relativePath } = useSite();
    const lang = locale.toUpperCase();
    const isSm = useMedia("(max-width: 1023px)");
    const [prevScrollPos, setPrevScrollPos] = useState(0);
    const [visibleBar, setVisibleBar] = useState(true);
    const [navScroll, setNavScroll] = useState(false);
    const [navMobileScroll, setNavMobileScroll] = useState(false);
    const [isNavMobileToggle, setIsNavMobileToggle] = useState(false);

    const { query, results, search, clearSearch, state } = useSearch({
        maxResults: 5,
    });

    const searchIsLoaded = state === SEARCH_STATE_LOADED;

    const onScroll = () => {
        let currentScrollPos = window.pageYOffset;
        if (
            [
                "template-product-services-v2",
                "Template_ProductServicesV2",
            ].includes(templateType)
        ) {
            if (isSm) {
                setNavMobileScroll(currentScrollPos >= 34);
                setNavScroll(currentScrollPos > 34);
            } else {
                setNavMobileScroll(currentScrollPos >= 99);
                setNavScroll(currentScrollPos > 99);
            }
        } else {
            setNavMobileScroll(currentScrollPos >= 99);
            setNavScroll(currentScrollPos > 99);
        }
    };

    useEffect(() => {
        if (typeof window !== "undefined") {
            window.addEventListener("scroll", onScroll);
        }
        return () => window.removeEventListener("scroll", onScroll);
    }, [prevScrollPos, visibleBar, onScroll]);

    // When the search visibility changes, we want to add an event listener that allows us to
    // detect when someone clicks outside of the search box, allowing us to close the results
    // when focus is drawn away from search

    useEffect(() => {
        // If we don't have a query, don't need to bother adding an event listener
        // but run the cleanup in case the previous state instance exists

        if (searchVisibility === SEARCH_HIDDEN) {
            removeDocumentOnClick();
            return;
        }

        addDocumentOnClick();
        addResultsRoving();

        // When the search box opens up, additionall find the search input and focus
        // on the element so someone can start typing right away

        const searchInput = Array.from(formRef.current.elements).find(
            input => input.type === "search",
        );

        searchInput.focus();

        return () => {
            removeResultsRoving();
            removeDocumentOnClick();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchVisibility]);

    /**
     * addDocumentOnClick
     */

    function addDocumentOnClick() {
        document.body.addEventListener("click", handleOnDocumentClick, true);
    }

    /**
     * removeDocumentOnClick
     */

    function removeDocumentOnClick() {
        document.body.removeEventListener("click", handleOnDocumentClick, true);
    }

    /**
     * handleOnDocumentClick
     */

    function handleOnDocumentClick(e) {
        if (!e.composedPath().includes(formRef.current)) {
            setSearchVisibility(SEARCH_HIDDEN);
            clearSearch();
        }
    }

    /**
     * handleOnSearch
     */

    function handleOnSearch({ currentTarget }) {
        search({
            query: currentTarget.value,
        });
    }

    /**
     * handleOnToggleSearch
     */

    function handleOnToggleSearch() {
        setSearchVisibility(SEARCH_VISIBLE);
    }

    /**
     * addResultsRoving
     */

    function addResultsRoving() {
        document.body.addEventListener("keydown", handleResultsRoving);
    }

    /**
     * removeResultsRoving
     */

    function removeResultsRoving() {
        document.body.removeEventListener("keydown", handleResultsRoving);
    }

    /**
     * handleResultsRoving
     */

    function handleResultsRoving(e) {
        const focusElement = document.activeElement;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (
                focusElement.nodeName === "INPUT" &&
                focusElement.nextSibling.children[0].nodeName !== "P"
            ) {
                focusElement.nextSibling.children[0].firstChild.firstChild.focus();
            } else if (focusElement.parentElement.nextSibling) {
                focusElement.parentElement.nextSibling.firstChild.focus();
            } else {
                focusElement.parentElement.parentElement.firstChild.firstChild.focus();
            }
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();
            if (
                focusElement.nodeName === "A" &&
                focusElement.parentElement.previousSibling
            ) {
                focusElement.parentElement.previousSibling.firstChild.focus();
            } else {
                focusElement.parentElement.parentElement.lastChild.firstChild.focus();
            }
        }
    }

    /**
     * escFunction
     */

    // pressing esc while search is focused will close it

    const escFunction = useCallback(event => {
        if (event.keyCode === 27) {
            clearSearch();
            setSearchVisibility(SEARCH_HIDDEN);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeLocale = locale => {
        if (router.lang === locale) return;
        let asPath = router.asPath;
        if (relativePath?.[locale] && relativePath?.[locale] !== null) {
            asPath = relativePath[locale];
        }
        router.push(router.pathname, asPath, { locale });
    };

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);
        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showChildMenu = id => {
        let element = document.getElementById(`list-child-${id}`);
        if (element.style.display === "block") {
            element.style.display = "none";
        } else {
            element.style.display = "block";
        }
    };

    useEffect(() => {
        (async () => {
            if (locale) {
                const menuPrimary = await getMenuBylocationV2(
                    locale.toLowerCase(),
                    "primary",
                );
                if (menuPrimary) {
                    setMenuPrimary(menuPrimary);
                }
            }
        })();
    }, [locale]);

    return !isSm ? (
        <nav
            className={`${styles.nav} ${navScroll ? styles.navScroll : ""} ${
                isFullColor ? styles.colorFull : ""
            }`}>
            <Section className={styles.navSection}>
                <div className={styles.navBrandWrap}>
                    <a href={lang === "TH" ? "/th" : "/"}>
                        <Logo />
                    </a>
                </div>
                <div
                    className={`${styles.navListMenuWrap} ${
                        navScroll ? styles.navListMenuWrapScroll : ""
                    }`}>
                    <div>
                        {!isPreview && (
                            <div className={styles.navLang}>
                                <span
                                    className={`${styles.navLangText} ${
                                        lang === "TH"
                                            ? styles.navLangTextBold
                                            : ""
                                    }`}
                                    onClick={() => changeLocale("th")}>
                                    TH
                                </span>
                                <span>|</span>
                                <span
                                    style={{ marginLeft: "5px" }}
                                    className={`${styles.navLangText} ${
                                        lang === "EN"
                                            ? styles.navLangTextBold
                                            : ""
                                    }`}
                                    onClick={() => changeLocale("en")}>
                                    EN
                                </span>
                            </div>
                        )}
                    </div>
                    <div
                        className={`${styles.navMenuWrap} ${
                            navScroll ? styles.navMenuWrapScroll : ""
                        }`}>
                        <ul className={styles.navMenu}>
                            {menuPrimary?.map(
                                ({
                                    id,
                                    path,
                                    label,
                                    title,
                                    target,
                                    children,
                                    type,
                                    url,
                                }) => {
                                    return (
                                        <li key={id}>
                                            {!path.includes("http") &&
                                                !target &&
                                                (type === "custom" ? (
                                                    <a href={url} title={title}>
                                                        <span>{label}</span>{" "}
                                                        {children?.length >
                                                        0 ? (
                                                            <Triangle />
                                                        ) : null}
                                                    </a>
                                                ) : (
                                                    <Link href={path}>
                                                        <a title={title}>
                                                            <span>{label}</span>{" "}
                                                            {children?.length >
                                                            0 ? (
                                                                <Triangle />
                                                            ) : null}
                                                        </a>
                                                    </Link>
                                                ))}
                                            {path.includes("http") &&
                                                (type === "custom" ? (
                                                    <a href={url} title={title}>
                                                        <span>{label}</span>{" "}
                                                        {children?.length >
                                                        0 ? (
                                                            <Triangle />
                                                        ) : null}
                                                    </a>
                                                ) : (
                                                    <a
                                                        href={path}
                                                        title={title}
                                                        target={target}>
                                                        <span>{label}</span>{" "}
                                                        {children?.length >
                                                        0 ? (
                                                            <Triangle />
                                                        ) : null}
                                                    </a>
                                                ))}

                                            {children?.length > 0 && (
                                                <ul
                                                    className={
                                                        styles.navSubMenu
                                                    }>
                                                    {children.map(
                                                        ({
                                                            id,
                                                            path,
                                                            label,
                                                            title,
                                                            target,
                                                            type,
                                                            url,
                                                        }) => {
                                                            return (
                                                                <li key={id}>
                                                                    {!path.includes(
                                                                        "http",
                                                                    ) &&
                                                                        !target &&
                                                                        (type ===
                                                                        "custom" ? (
                                                                            <a
                                                                                href={
                                                                                    url
                                                                                }
                                                                                title={
                                                                                    title
                                                                                }>
                                                                                {
                                                                                    label
                                                                                }
                                                                            </a>
                                                                        ) : (
                                                                            <Link
                                                                                href={
                                                                                    path
                                                                                }>
                                                                                <a
                                                                                    title={
                                                                                        title
                                                                                    }>
                                                                                    {
                                                                                        label
                                                                                    }
                                                                                </a>
                                                                            </Link>
                                                                        ))}
                                                                    {path.includes(
                                                                        "http",
                                                                    ) &&
                                                                        (type ===
                                                                        "custom" ? (
                                                                            <a
                                                                                href={
                                                                                    url
                                                                                }
                                                                                title={
                                                                                    title
                                                                                }>
                                                                                {
                                                                                    label
                                                                                }
                                                                            </a>
                                                                        ) : (
                                                                            <a
                                                                                href={
                                                                                    path
                                                                                }
                                                                                title={
                                                                                    title
                                                                                }
                                                                                target={
                                                                                    target
                                                                                }>
                                                                                {
                                                                                    label
                                                                                }
                                                                            </a>
                                                                        ))}
                                                                </li>
                                                            );
                                                        },
                                                    )}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                },
                            )}
                        </ul>
                        {SHOW_SEARCH && (
                            <div className={styles.navSearch}>
                                {searchVisibility === SEARCH_HIDDEN && (
                                    <button
                                        onClick={handleOnToggleSearch}
                                        disabled={!searchIsLoaded}>
                                        <span className="sr-only">
                                            Toggle Search
                                        </span>
                                        <FaSearch />
                                    </button>
                                )}
                                {searchVisibility === SEARCH_VISIBLE && (
                                    <form
                                        ref={formRef}
                                        action="/search"
                                        data-search-is-active={!!query}>
                                        <input
                                            type="search"
                                            name="q"
                                            value={query || ""}
                                            onChange={handleOnSearch}
                                            autoComplete="off"
                                            placeholder="Search..."
                                            required
                                        />
                                        <div
                                            className={styles.navSearchResults}>
                                            {results.length > 0 && (
                                                <ul>
                                                    {results.map(
                                                        (
                                                            { slug, title },
                                                            index,
                                                        ) => {
                                                            return (
                                                                <li key={slug}>
                                                                    <Link
                                                                        tabIndex={
                                                                            index
                                                                        }
                                                                        href={postPathBySlug(
                                                                            slug,
                                                                        )}>
                                                                        <a>
                                                                            {
                                                                                title
                                                                            }
                                                                        </a>
                                                                    </Link>
                                                                </li>
                                                            );
                                                        },
                                                    )}
                                                </ul>
                                            )}
                                            {results.length === 0 && (
                                                <p>
                                                    Sorry, not finding anything
                                                    for <strong>{query}</strong>
                                                </p>
                                            )}
                                        </div>
                                    </form>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Section>
        </nav>
    ) : (
        <nav
            className={`${styles.navMobile} ${
                navMobileScroll ? styles.navMobileScroll : ""
            } ${isFullColor ? styles.colorFull : ""}`}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    width: "110px",
                }}>
                <a href={lang === "TH" ? "/th" : "/"}>
                    <Logo customWidth={110} customHeight={50} />
                </a>
            </div>
            <div>
                <div
                    style={{ width: 40, height: 40 }}
                    onClick={() => setIsNavMobileToggle(!isNavMobileToggle)}>
                    <Hamburger />
                </div>
            </div>
            <div
                className={`${styles.navMobileMenuWrap} ${
                    isNavMobileToggle
                        ? styles.navMobileToggle
                        : styles.navMobileClose
                }`}>
                <div className={styles.hambugerClose}>
                    <span
                        onClick={() =>
                            setIsNavMobileToggle(!isNavMobileToggle)
                        }>
                        <Hamburger type="close" />
                    </span>
                </div>
                <ul className={styles.menuList}>
                    {menuPrimary?.map(
                        ({
                            id,
                            path,
                            label,
                            title,
                            target,
                            children,
                            type,
                            url,
                        }) => {
                            return (
                                <li className={styles.menuItem} key={id}>
                                    {!path.includes("http") &&
                                        !target &&
                                        (type === "custom" ? (
                                            <a
                                                href={url}
                                                title={title}
                                                onClick={() =>
                                                    setTimeout(
                                                        () =>
                                                            setIsNavMobileToggle(
                                                                !isNavMobileToggle,
                                                            ),
                                                        300,
                                                    )
                                                }>
                                                <span>{label}</span>{" "}
                                                {children?.length > 0
                                                    ? null
                                                    : null}
                                            </a>
                                        ) : (
                                            <Link href={path}>
                                                <a
                                                    title={title}
                                                    onClick={() =>
                                                        setTimeout(
                                                            () =>
                                                                setIsNavMobileToggle(
                                                                    !isNavMobileToggle,
                                                                ),
                                                            300,
                                                        )
                                                    }>
                                                    <span>{label}</span>{" "}
                                                    {children?.length > 0
                                                        ? null
                                                        : null}
                                                </a>
                                            </Link>
                                        ))}
                                    {path.includes("http") && (
                                        <a
                                            href={path}
                                            title={title}
                                            target={target}
                                            onClick={() =>
                                                setTimeout(
                                                    () =>
                                                        setIsNavMobileToggle(
                                                            !isNavMobileToggle,
                                                        ),
                                                    300,
                                                )
                                            }>
                                            <span>{label}</span>{" "}
                                            {children?.length > 0 ? null : null}
                                        </a>
                                    )}
                                    {children?.length > 0 && (
                                        <div
                                            id={`${id}`}
                                            className={styles.iconWrap}
                                            onClick={() => showChildMenu(id)}>
                                            <FontAwesomeIcon
                                                className={styles.icon}
                                                icon={faCaretDown}
                                                size="sm"
                                            />
                                        </div>
                                    )}
                                    {children?.length > 0 && (
                                        <ul
                                            className={`${styles.menuItemChildWrap}`}
                                            id={`list-child-${id}`}>
                                            {children.map(
                                                ({
                                                    id,
                                                    path,
                                                    label,
                                                    title,
                                                    target,
                                                    type,
                                                    url,
                                                }) => {
                                                    return (
                                                        <li
                                                            key={id}
                                                            className={
                                                                styles.menuItemChild
                                                            }>
                                                            {!path.includes(
                                                                "http",
                                                            ) &&
                                                                !target &&
                                                                (type ===
                                                                "custom" ? (
                                                                    <a
                                                                        href={
                                                                            url
                                                                        }
                                                                        title={
                                                                            title
                                                                        }
                                                                        onClick={() =>
                                                                            setTimeout(
                                                                                () =>
                                                                                    setIsNavMobileToggle(
                                                                                        !isNavMobileToggle,
                                                                                    ),
                                                                                300,
                                                                            )
                                                                        }>
                                                                        {label}
                                                                    </a>
                                                                ) : (
                                                                    <Link
                                                                        href={
                                                                            path
                                                                        }>
                                                                        <a
                                                                            title={
                                                                                title
                                                                            }
                                                                            onClick={() =>
                                                                                setTimeout(
                                                                                    () =>
                                                                                        setIsNavMobileToggle(
                                                                                            !isNavMobileToggle,
                                                                                        ),
                                                                                    300,
                                                                                )
                                                                            }>
                                                                            {
                                                                                label
                                                                            }
                                                                        </a>
                                                                    </Link>
                                                                ))}
                                                            {path.includes(
                                                                "http",
                                                            ) &&
                                                                (type ===
                                                                "custom" ? (
                                                                    <a
                                                                        href={
                                                                            url
                                                                        }
                                                                        title={
                                                                            title
                                                                        }
                                                                        onClick={() =>
                                                                            setTimeout(
                                                                                () =>
                                                                                    setIsNavMobileToggle(
                                                                                        !isNavMobileToggle,
                                                                                    ),
                                                                                300,
                                                                            )
                                                                        }>
                                                                        {label}
                                                                    </a>
                                                                ) : (
                                                                    <a
                                                                        href={
                                                                            path
                                                                        }
                                                                        title={
                                                                            title
                                                                        }
                                                                        target={
                                                                            target
                                                                        }
                                                                        onClick={() =>
                                                                            setTimeout(
                                                                                () =>
                                                                                    setIsNavMobileToggle(
                                                                                        !isNavMobileToggle,
                                                                                    ),
                                                                                300,
                                                                            )
                                                                        }>
                                                                        {label}
                                                                    </a>
                                                                ))}
                                                        </li>
                                                    );
                                                },
                                            )}
                                        </ul>
                                    )}
                                </li>
                            );
                        },
                    )}
                </ul>
                {!isPreview && (
                    <div className={styles.navLang}>
                        <span
                            className={`${styles.navLangText} ${
                                lang === "TH" ? styles.navLangTextBold : ""
                            }`}
                            onClick={() => changeLocale("th")}>
                            TH
                        </span>
                        <span>|</span>
                        <span
                            style={{ marginLeft: "5px" }}
                            className={`${styles.navLangText} ${
                                lang === "EN" ? styles.navLangTextBold : ""
                            }`}
                            onClick={() => changeLocale("en")}>
                            EN
                        </span>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Nav;
